import React, { useRef, useState, useEffect } from "react";
import { bem, ButtonContainer, ButtonPrimary, Empty, Hyperlink, qaId, Title } from "../../ui-library";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { useEffectOnce } from "react-use";
import { useTranslation } from "react-i18next";

import { selectAuthentication } from "../../state/tree/authentication/selectors";
import { selectUserProfile } from "../../state/tree/user/selectors";
import { useAppDispatch } from "../../state/RootReducer";
import { signOut } from "../../state/tree/authentication/thunks";
import IsomorphicSuspense from "../../classes/IsomorphicSuspense";
import { selectMapOptionsDisplayed } from "../../state/tree/map-page/map-controls/selectors";

import { ReactComponent as IcUserReported24 } from "../../../resources/icons/ic-user-reported-24.svg";
import { ReactComponent as IcUserReported16 } from "../../../resources/icons/ic-user-reported-16.svg";

import "./LoginComponent.scss";
import { selectExternalLinks } from "../../state/tree/global-configuration/selectors";
import { supportedLocale } from "../../classes/localization/Localization";
import i18next from "i18next";
import { getLoginUrl, getLogoutUrl, samlLogoutRedirect } from "../../utils/authentication";
import useGetHostname from "../../../src/hooks/useGetHostname";
import { selectRetryAction } from "../../../src/state/tree/application/selectors";

const loginComponentBem = bem("login-component");
const loginComponentQA = qaId("login-component");
const loggedInCardBem = bem("logged-in-card");
const loggedInCardQA = qaId("logged-in-card");

type LoginComponentProps = {
    logoutId?: string;
};

const LoginCmp = ({ logoutId }: LoginComponentProps) => {
    const [showLoggedInCard, setShowLoggedInCard] = useState(false);
    const hostname = useGetHostname();
    const authenticated = useSelector(selectAuthentication)?.authenticated;
    const userProfile = useSelector(selectUserProfile);
    const mapOptionsDisplayed = useSelector(selectMapOptionsDisplayed);
    const externalLinks = useSelector(selectExternalLinks);
    const retryAction = useSelector(selectRetryAction);

    const dispatch = useAppDispatch();
    const { t } = useTranslation("LoginComponent");

    const handleSignOut = () => {
        setShowLoggedInCard(false);
        return dispatch(signOut(true));
    };

    // https://blog.logrocket.com/detect-click-outside-react-component-how-to/
    const ref = useRef(null);
    useEffectOnce(() => {
        document.addEventListener(
            "mousedown",
            (event) => {
                if (ref.current && !ref.current.contains(event.target)) {
                    setShowLoggedInCard(false);
                }
            },
            true
        );
    });

    useEffect(() => {
        // If a retry action is set (user attempted a protected action while unauthenticated), redirect to the SSO login page.
        if (retryAction && hostname) {
            window.location.href = getLoginUrl(hostname);
        }
    }, [retryAction, hostname]);

    useEffect(() => {
        if (!logoutId) {
            return;
        }

        // Signout for navcloud/GOR session only. Must COMPLETE before the SAML logout redirect: samlLogoutRedirect()
        // does a form.submit(), and the resulting navigation aborts any in-flight fetch.
        const signOutAndRedirect = async () => {
            try {
                await handleSignOut();
            } catch (e) {
                // Never block the SAML logout redirect on the GOR signout failing.
            }

            samlLogoutRedirect(hostname, logoutId);
        };

        signOutAndRedirect();
    }, [logoutId, hostname]);

    return (
        <div
            ref={ref}
            className={classNames(
                loginComponentBem.block(),
                mapOptionsDisplayed && loginComponentBem.modifier("map-layers-panel-open")
            )}
            {...loginComponentQA.block()}
        >
            {authenticated ? (
                <ButtonContainer
                    onClick={() => setShowLoggedInCard(!showLoggedInCard)}
                    {...loginComponentQA.element("logged-in-icon")}
                >
                    <IcUserReported24
                        className={classNames(loginComponentBem.element("logged-in-icon"), {
                            [loginComponentBem.modifier("active")]: showLoggedInCard
                        })}
                    />
                </ButtonContainer>
            ) : (
                <ButtonPrimary
                    className={loginComponentBem.element("login-button")}
                    icon={<IcUserReported16 />}
                    text={t("LoginComponent:sign_in")}
                    href={getLoginUrl(hostname)}
                    {...loginComponentQA.element("login-button")}
                />
            )}
            {showLoggedInCard && (
                <div className={loggedInCardBem.block()} {...loggedInCardQA.block()}>
                    <Title size="s" className={loggedInCardBem.element("title")} {...loggedInCardQA.element("title")}>
                        {userProfile.username}
                    </Title>
                    <a
                        href={`${externalLinks.tomtomURL}/${supportedLocale[i18next.language]}/${
                            externalLinks.account_details
                        }`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={loggedInCardBem.element("account-link")}
                        {...loggedInCardQA.element("account-link")}
                    >
                        <Hyperlink>{t("LoginComponent:manage")}</Hyperlink>
                    </a>
                    <div className={loggedInCardBem.element("actions")}>
                        <ButtonPrimary
                            className={loggedInCardBem.element("logout-button")}
                            href={getLogoutUrl(hostname)}
                            text={t("LoginComponent:sign_out")}
                            {...loggedInCardQA.element("logout-button")}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const LoginComponent = (props: LoginComponentProps) => (
    <IsomorphicSuspense fallback={<Empty />}>
        <LoginCmp {...props} />
    </IsomorphicSuspense>
);

export default LoginComponent;
